/**
 * The one place a credit figure becomes text.
 *
 * Before this existed the same row could show "14,000" in one cell and
 * "15000.00" in the next — same unit, two renderings, because each call
 * site picked its own between `toLocaleString` and `toFixed`. Grouping
 * and decimals are presentation decisions, so they belong together in
 * one function rather than being re-decided per cell.
 */

export type FormatCreditsOptions = {
  /** Intl tag, normally intlLocale(). Grouping is language-specific:
   *  Swedish writes 5 000 where English writes 5,000. Defaulting to the
   *  browser's locale put "14,000" beside a Swedish "1 000 credits" on
   *  the same card — two separators for one unit on one screen, so
   *  there is no default. */
  locale: string;
  /** Fixed decimal places. Balances are whole; ledger amounts are 2. */
  decimals?: number;
  /** Show a leading + on positive values. For signed deltas, where the
   *  difference between a spend and a top-up is the whole point. */
  signed?: boolean;
};

export function formatCredits(
  credits: number,
  options: FormatCreditsOptions,
): string {
  const { decimals = 0, signed = false, locale } = options;
  // A NaN or Infinity reaching a balance cell is a bug upstream, but
  // rendering "NaN" to a customer is worse than rendering a zero.
  const safe = Number.isFinite(credits) ? credits : 0;
  return safe.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? "exceptZero" : "auto",
  });
}
